<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const VALIDATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'malaysia', label: 'Malaysia (e.g. 990101-14-5678)' },
  { value: 'thailand', label: 'Thailand (13 digits)' },
  { value: 'custom', label: 'Custom regex' },
];

const validationType = ref('none');
const customRegex = ref('');
const isSubmitting = ref(false);

const showCustomInput = computed(() => validationType.value === 'custom');

watch(
  currentAccount,
  () => {
    const { ic_number_validation, ic_number_custom_regex } =
      currentAccount.value?.settings || {};
    const knownValues = ['malaysia', 'thailand', 'none'];
    if (ic_number_validation && knownValues.includes(ic_number_validation)) {
      validationType.value = ic_number_validation;
    } else if (ic_number_validation && ic_number_validation !== '' && ic_number_validation !== null) {
      // Treat unknown stored value as custom
      validationType.value = 'custom';
    } else {
      validationType.value = 'none';
    }
    customRegex.value = ic_number_custom_regex || '';
  },
  { immediate: true }
);

const handleSubmit = async () => {
  const payload = {
    ic_number_validation: validationType.value,
    ic_number_custom_regex: validationType.value === 'custom' ? customRegex.value : '',
  };
  try {
    isSubmitting.value = true;
    await updateAccount(payload, { silent: true });
    useAlert('IC Number validation settings saved.');
  } catch {
    useAlert('Failed to save IC Number validation settings.');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    title="IC Number Validation"
    description="Set the validation format for the IC Number field used in contact forms."
    with-border
  >
    <form class="grid gap-5" @submit.prevent="handleSubmit">
      <WithLabel
        label="Validation Format"
        help-message="Choose the country format or provide a custom regular expression."
      >
        <select v-model="validationType" class="ic-select w-full">
          <option
            v-for="opt in VALIDATION_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </WithLabel>

      <WithLabel
        v-if="showCustomInput"
        label="Custom Regex Pattern"
        help-message="Enter a regular expression, e.g. ^\d{12}$"
      >
        <input
          v-model="customRegex"
          type="text"
          class="ic-text-input w-full"
          placeholder="^\d{12}$"
        />
      </WithLabel>

      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          label="Save IC Number Settings"
        />
      </div>
    </form>
  </SectionLayout>
</template>

<style scoped>
.ic-select {
  appearance: none;
  background-color: #ffffff;
  border: none;
  outline: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: #111827;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.ic-select:hover {
  outline-color: #9ca3af;
}

.ic-select:focus {
  outline: 2px solid #0066ff;
  outline-offset: -1px;
}

.ic-text-input {
  background-color: #ffffff;
  border: none;
  outline: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: #111827;
}

.ic-text-input:focus {
  outline: 2px solid #0066ff;
  outline-offset: -1px;
}

.ic-text-input::placeholder {
  color: #94a3b8;
}
</style>
